"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Store = exports.StoreEvent = void 0;
const p_queue_1 = __importDefault(require("p-queue"));
const event_emitter_1 = require("../event-emitter");
var StoreEvent;
(function (StoreEvent) {
    StoreEvent["ItemUpdated"] = "ITEM_UPDATED";
})(StoreEvent = exports.StoreEvent || (exports.StoreEvent = {}));
// This queue is used to ensure only one update is run at a time (hence the
// concurrency of 1). This keeps async updates from unintentionally overwriting
// each other.
const updateQueue = new p_queue_1.default({ concurrency: 1 });
/**
 * This abstract class defines a normalized API for key-value storage that can
 * be extended by any number of storage solutions.
 */
class Store extends event_emitter_1.EventEmitter {
    constructor(options) {
        super();
        /**
         * Run through all of the provided migrations and apply then one by one (if
         * they haven't been applied before).
         */
        this.applyMigrations = async (options) => {
            var _a;
            // Bail if there are no migrations to apply
            if (!((_a = options.migrations) === null || _a === void 0 ? void 0 : _a.length))
                return;
            // We need to create a couple new instances of the same store type. However
            // TypeScript doesn't understand that this.constructor will reference the
            // _subclass_ of Store that we are currently dealing with. Instead it thinks
            // we are trying to instantiate an abstract class and tries to blow things
            // up. `any` to the rescue, unfortunately.
            //
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const StoreType = this.constructor;
            // Create a store where we can track whether the migrations have been
            // applied or not
            const migrationStore = new StoreType({
                key: "migrations." + this.key,
            });
            // Clone the current store (but without any migrations specified) and apply
            // the migrations one by one
            const store = new StoreType({ key: this.key });
            for (const migration of options.migrations) {
                const migrationHasRun = await migrationStore.get(migration.name);
                if (!migrationHasRun) {
                    await migration.migrate(store);
                    await migrationStore.set(migration.name, true);
                }
            }
        };
        this.prefixKey = (key) => {
            return this.key + "." + key;
        };
        this.getKeysWithoutPrefixes = (prefixedKeys) => {
            return prefixedKeys
                .filter((key) => key.startsWith(this.key + "."))
                .map((key) => key.replace(this.key + ".", ""));
        };
        /**
         * Set a value in the store under a given key. If the provided value is null
         * or undefined then this is equivalent to calling the `remove` method.
         */
        this.set = async (key, value) => {
            await this.initialized;
            if (value === null || value === undefined) {
                await this._remove(this.prefixKey(key));
            }
            else {
                await this._set(this.prefixKey(key), value);
            }
            this.emitter.emit(StoreEvent.ItemUpdated, { key, value: value !== null && value !== void 0 ? value : null });
        };
        /**
         * Fetch a value from the store given a key. Null is returned if the value
         * does not exist at the given key.
         */
        this.get = async (key) => {
            var _a;
            await this.initialized;
            const value = await this._get(this.prefixKey(key));
            return (_a = value) !== null && _a !== void 0 ? _a : null;
        };
        /**
         * Completely remove a key/value pair from the store.
         */
        this.remove = async (key) => {
            await this.initialized;
            await this._remove(this.prefixKey(key));
            this.emitter.emit(StoreEvent.ItemUpdated, { key, value: null });
        };
        /*
         * Update an item in the store. This is an atomic operation - only one call to
         * `update` will run at a time and each call will receive the latest data in
         * the store. If `undefined` is returned from the update function the write
         * will be skipped. Note that calls to `set` and `remove` are not syncronized
         * with calls to `update` and may interfere with updates.
         */
        this.update = async (key, update) => {
            await this.initialized;
            return updateQueue.add(async () => {
                const value = update(await this.get(key));
                if (value !== undefined) {
                    await this.set(key, value);
                }
            });
        };
        /**
         * Get an array containing all keys in the store.
         */
        this.keys = async () => {
            await this.initialized;
            const prefixedKeys = await this._keys();
            return this.getKeysWithoutPrefixes(prefixedKeys);
        };
        /**
         * Fetch multiple values from the store at once given an array of keys. An
         * array of values is returned.
         */
        this.getMany = async (keys) => {
            await this.initialized;
            const value = await this._getMany(keys.map(this.prefixKey));
            return value;
        };
        /**
         * Remove multiple items from the store at once given an array of keys.
         */
        this.removeMany = async (keys) => {
            await this.initialized;
            await this._removeMany(keys.map(this.prefixKey));
        };
        /**
         * Delete all items in the store
         */
        this.clear = async () => {
            await this.initialized;
            const keys = await this.keys();
            await this.removeMany(keys);
        };
        this.key = options.key;
        this.initialized = this.applyMigrations(options);
    }
}
exports.Store = Store;
